import { zodResolver } from '@hookform/resolvers/zod';
import { UploadSimple } from '@phosphor-icons/react';
import type { PersonaDto } from '@reelplicate/api-client/model';
import {
  getListPersonasQueryKey,
  useCreatePersona,
  useCreatePersonaAvatarUpload,
  useRemovePersonaAvatar,
  useSetPersonaAvatar,
  useUpdatePersona,
} from '@reelplicate/api-client/personas';
import {
  Avatar,
  Button,
  Field,
  Inline,
  Modal,
  ModalClose,
  ModalContent,
  Stack,
  TextArea,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { IMAGE_TYPES, imageProblem, type ImageType, putToSignedUrl } from '@/lib/upload';

import { PersonaSchema, type PersonaValues, toValues } from './schema';
import { PersonaTimezoneField } from './timezone-field';

/**
 * Add or edit a persona: avatar, name, short name and voice. A picked avatar
 * previews at once and uploads after the persona saves (a new persona needs
 * its id first).
 */
export function PersonaModal({
  workspaceId,
  persona,
  uploadsEnabled,
  open,
  onOpenChange,
  onCreated,
}: {
  workspaceId: string;
  /** The persona to edit; omit to add one. */
  persona?: PersonaDto;
  uploadsEnabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** After adding (not editing) one, e.g. to open its page. */
  onCreated?: (persona: PersonaDto) => void;
}) {
  const formId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [avatar, setAvatar] = useState<{ file: File; preview: string } | null>(null);
  const [clearAvatar, setClearAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const create = useCreatePersona();
  const update = useUpdatePersona();
  const createUpload = useCreatePersonaAvatarUpload();
  const setAvatarKey = useSetPersonaAvatar();
  const removeAvatar = useRemovePersonaAvatar();
  const form = useForm<PersonaValues>({
    resolver: zodResolver(PersonaSchema),
    defaultValues: toValues(persona),
  });

  useEffect(() => {
    if (open) form.reset(toValues(persona));
  }, [open, persona, form]);

  /** Closing drops a picked-but-unsaved avatar, so reopening starts clean. */
  const changeOpen = (next: boolean) => {
    if (!next) {
      setAvatar(null);
      setClearAvatar(false);
    }
    onOpenChange(next);
  };

  useEffect(
    () => () => {
      if (avatar) URL.revokeObjectURL(avatar.preview);
    },
    [avatar],
  );

  const name = useWatch({ control: form.control, name: 'name' });
  const shownAvatar = avatar?.preview ?? (clearAvatar ? undefined : persona?.avatarUrl);

  const pick = async (file: File) => {
    const problem = await imageProblem(file, { what: 'avatar', minPx: 128 });
    if (problem) {
      toast.error(problem);
      return;
    }
    setAvatar({ file, preview: URL.createObjectURL(file) });
    setClearAvatar(false);
  };

  const uploadAvatar = async (personaId: string, file: File) => {
    const { url, key } = await createUpload.mutateAsync({
      id: workspaceId,
      personaId,
      data: { contentType: file.type as ImageType, size: file.size },
    });
    await putToSignedUrl(url, file);
    await setAvatarKey.mutateAsync({ id: workspaceId, personaId, data: { key } });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setSaving(true);
    try {
      const saved = persona
        ? await update.mutateAsync({
            id: workspaceId,
            personaId: persona.id,
            data: values,
          })
        : await create.mutateAsync({ id: workspaceId, data: values });
      try {
        if (avatar) await uploadAvatar(saved.id, avatar.file);
        else if (clearAvatar && persona?.avatarUrl) {
          await removeAvatar.mutateAsync({ id: workspaceId, personaId: saved.id });
        }
      } catch (error) {
        // The persona saved; only the picture didn't. API failures already toasted.
        if (error instanceof Error && error.message.startsWith('Upload failed')) {
          toast.error('The avatar didn’t upload. Try again.');
        }
      }
      await queryClient.invalidateQueries({
        queryKey: getListPersonasQueryKey(workspaceId),
      });
      toast.success(persona ? 'Persona saved' : `${saved.name} added`);
      changeOpen(false);
      if (!persona) onCreated?.(saved);
    } catch {
      // The failure is already shown as a toast.
    } finally {
      setSaving(false);
    }
  });

  return (
    <Modal open={open} onOpenChange={changeOpen}>
      <ModalContent
        title={persona ? `Edit ${persona.name}` : 'Add persona'}
        description="A face your brand posts as, with its own accounts and voice."
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button type="submit" form={formId} loading={saving}>
              {persona ? 'Save persona' : 'Add persona'}
            </Button>
          </>
        }
      >
        <Stack
          as="form"
          id={formId}
          gap={5}
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Inline gap={4}>
            <Avatar
              size={56}
              name={name || persona?.name}
              src={shownAvatar ?? undefined}
              alt=""
            />
            <Stack gap={0.5} className="min-w-0 flex-1">
              <p className="text-body-md-strong text-text-primary">Avatar</p>
              <p className="text-body-sm text-text-secondary">
                {uploadsEnabled
                  ? 'Square image, at least 128 px. Shown on the calendar and in analytics.'
                  : 'Uploads aren’t set up yet.'}
              </p>
            </Stack>
            {shownAvatar && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setAvatar(null);
                  setClearAvatar(true);
                }}
              >
                Remove
              </Button>
            )}
            <input
              ref={input}
              type="file"
              accept={IMAGE_TYPES.join(',')}
              className="sr-only"
              tabIndex={-1}
              aria-label="Avatar file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void pick(file);
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              leadingIcon={UploadSimple}
              disabled={!uploadsEnabled}
              onClick={() => input.current?.click()}
            >
              Upload
            </Button>
          </Inline>

          <Stack gap={4} className="sm:flex-row sm:items-start">
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Name" error={fieldState.error?.message} className="flex-1">
                  <TextInput
                    {...field}
                    placeholder="e.g. Barista Bea"
                    autoComplete="off"
                  />
                </Field>
              )}
            />
            <Controller
              name="shortName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  label="Short name"
                  helper="On calendar chips. Optional."
                  error={fieldState.error?.message}
                  className="sm:w-44"
                >
                  <TextInput {...field} placeholder="Bea" autoComplete="off" />
                </Field>
              )}
            />
          </Stack>

          <Controller
            name="voice"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Voice"
                helper="Who they are and how they talk. Used alongside your brand profile when writing for them."
                error={fieldState.error?.message}
              >
                <TextArea
                  {...field}
                  rows={4}
                  placeholder="Head barista. Warm, a little nerdy about milk texture, never salesy."
                />
              </Field>
            )}
          />

          <Controller
            name="timezone"
            control={form.control}
            render={({ field, fieldState }) => (
              <PersonaTimezoneField
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      </ModalContent>
    </Modal>
  );
}
